import type { MouseEvent, ReactNode } from "react";
import { cx } from "../cx";
import { SmartCityMark } from "../icons";

export interface NavItem {
  id: string;
  label: string;
  href: string;
  icon?: ReactNode;
  /** Sub-items, shown nested under this item while it (or one of them) is the current section. */
  children?: NavItem[];
}

export interface NavGroup {
  id: string;
  /** Group heading ("Services", "Hub"); omit for the top items. */
  label?: string;
  items: NavItem[];
}

export interface SideNavProps {
  brand: { title: string; subtitle?: string; mark?: ReactNode };
  groups: NavGroup[];
  /** Id of the current page's item (a top item or a sub-item). */
  currentId?: string | null;
  /** Client-side navigation for plain left clicks; modified clicks keep the browser's behaviour. */
  onNavigate?: (href: string, item: NavItem) => void;
  /** Bottom of the sidebar (the signed-in person). */
  footer?: ReactNode;
  label?: string;
}

function contains(item: NavItem, id: string | null | undefined): boolean {
  return !!id && !!item.children?.some((c) => c.id === id);
}

/**
 * Sidebar navigation from the mockups: brand tile, groups, 40 px items with icons. The current page has
 * `aria-current="page"` (black pill); the open module shows its sub-items indented, the current one in bold.
 */
export function SideNav({ brand, groups, currentId, onNavigate, footer, label = "Main" }: SideNavProps) {
  function link(item: NavItem, sub: boolean) {
    const isPage = item.id === currentId;
    const inSection = !sub && contains(item, currentId);
    const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (!onNavigate || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      onNavigate(item.href, item);
    };
    return (
      <a
        href={item.href}
        className={cx("lcc-nav__link", inSection && "lcc-nav__link--section")}
        aria-current={isPage ? "page" : inSection ? "true" : undefined}
        onClick={onClick}
      >
        {!sub && item.icon}
        <span>{item.label}</span>
      </a>
    );
  }

  return (
    <>
      <div className="lcc-brand">
        <span className="lcc-brand__mark">{brand.mark ?? <SmartCityMark />}</span>
        <span className="lcc-brand__text">
          <span className="lcc-brand__title">{brand.title}</span>
          {brand.subtitle && <span className="lcc-brand__subtitle">{brand.subtitle}</span>}
        </span>
      </div>
      <nav className="lcc-nav" aria-label={label}>
        {groups.map((g) =>
          g.items.length === 0 ? null : (
            <div key={g.id}>
              {g.label && (
                <p className="lcc-nav__group" id={`lcc-nav-${g.id}`}>
                  {g.label}
                </p>
              )}
              <ul className="lcc-nav__list" aria-labelledby={g.label ? `lcc-nav-${g.id}` : undefined}>
                {g.items.map((item) => {
                  const open = item.id === currentId || contains(item, currentId);
                  return (
                    <li key={item.id}>
                      {link(item, false)}
                      {open && item.children && item.children.length > 0 && (
                        <ul className="lcc-nav__list lcc-nav__sub" aria-label={`${item.label} sections`}>
                          {item.children.map((c) => (
                            <li key={c.id}>{link(c, true)}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ),
        )}
      </nav>
      {footer && <div className="lcc-nav__footer">{footer}</div>}
    </>
  );
}
